/**
 * Encrypted SQLite (SQLCipher) implementation of OfflineStorage for the
 * iOS/Android store apps (ADR-0021), via @capacitor-community/sqlite.
 *
 * UNTESTED ON DEVICE. Calls are type-checked against the real plugin
 * (@capacitor-community/sqlite ^8). Before shipping the store apps: `npx cap
 * sync`, then run the storage contract tests on a device/emulator. There is no
 * schema-versioning here yet: tables are JSON blobs keyed by id; a future
 * shape change follows the same rule as lib/offline/db.ts (never drop the
 * outbox, migrate rows in place).
 *
 * Encryption: SQLCipher encrypts the whole database file, so — unlike the
 * IndexedDB adapter — rows are not sealed individually. On first run a random
 * 256-bit passphrase is handed to the plugin's `setEncryptionSecret`, which
 * keeps it in the platform secure store (iOS Keychain under the
 * `iosKeychainPrefix`; Android EncryptedSharedPreferences, whose master key
 * lives in the Android Keystore). Connections then open in "secret" mode and
 * the passphrase never touches JS storage. Requires `iosIsEncryption` /
 * `androidIsEncryption: true` in capacitor.config.ts (set). The passphrase is
 * not additionally wrapped with a WebCrypto key: the plugin's secure store is
 * the platform mechanism for this, and a WebView key would be weaker.
 *
 * Wipe = crypto-shredding: delete the database file and clear the secret, so
 * pages SQLite may leave on flash are unreadable; a new secret and file are
 * created for the next sign-in.
 */
import { registerPlugin } from '@capacitor/core';
import type { CapacitorSQLitePlugin } from '@capacitor-community/sqlite';
import type { OutboxItem } from '@sss/sync';
import type { CarriedFinding, PackCustomer, PackSite, PackTemplateVersion, SyncPack } from '../api/types';
import { siteMatches, type AttachmentRecord, type DraftRecord, type OfflineStorage } from './types';

// Typed against the real plugin; reached through registerPlugin so the PWA bundle
// doesn't carry the plugin's web implementation (it is only used on native).
const CapacitorSQLite = registerPlugin<CapacitorSQLitePlugin>('CapacitorSQLite');

const DB = 'sss_offline';

const SCHEMA = `
CREATE TABLE IF NOT EXISTS kv (key TEXT PRIMARY KEY NOT NULL, value TEXT);
CREATE TABLE IF NOT EXISTS sites (id TEXT PRIMARY KEY NOT NULL, data TEXT NOT NULL);
CREATE TABLE IF NOT EXISTS customers (id TEXT PRIMARY KEY NOT NULL, data TEXT NOT NULL);
CREATE TABLE IF NOT EXISTS templates (id TEXT PRIMARY KEY NOT NULL, data TEXT NOT NULL);
CREATE TABLE IF NOT EXISTS findings (id TEXT PRIMARY KEY NOT NULL, site_id TEXT NOT NULL, data TEXT NOT NULL);
CREATE TABLE IF NOT EXISTS drafts (id TEXT PRIMARY KEY NOT NULL, updated_at INTEGER NOT NULL, data TEXT NOT NULL);
CREATE TABLE IF NOT EXISTS attachments (id TEXT PRIMARY KEY NOT NULL, survey_id TEXT NOT NULL, meta TEXT NOT NULL, body TEXT NOT NULL);
CREATE TABLE IF NOT EXISTS outbox (id TEXT PRIMARY KEY NOT NULL, seq INTEGER NOT NULL, data TEXT NOT NULL);
CREATE INDEX IF NOT EXISTS findings_site ON findings (site_id);
CREATE INDEX IF NOT EXISTS attachments_survey ON attachments (survey_id);
`;

function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

function fromBase64(b64: string): ArrayBuffer {
  const s = atob(b64);
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
  return bytes.buffer;
}

export class SqliteStorage implements OfflineStorage {
  readonly kind = 'sqlite' as const;
  private ready = false;

  constructor(private readonly plugin: CapacitorSQLitePlugin = CapacitorSQLite) {}

  async init(): Promise<void> {
    if (this.ready) return;
    const { result } = await this.plugin.isSecretStored();
    if (!result) {
      const bytes = crypto.getRandomValues(new Uint8Array(32));
      await this.plugin.setEncryptionSecret({ passphrase: Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('') });
    }
    await this.plugin.createConnection({ database: DB, version: 1, encrypted: true, mode: 'secret', readonly: false });
    await this.plugin.open({ database: DB, readonly: false });
    await this.plugin.execute({ database: DB, statements: SCHEMA, readonly: false });
    this.ready = true;
  }

  private async rows(statement: string, values: unknown[] = []): Promise<Record<string, unknown>[]> {
    const r = await this.plugin.query({ database: DB, statement, values, readonly: false });
    return r.values ?? [];
  }

  private run(statement: string, values: unknown[] = []): Promise<unknown> {
    return this.plugin.run({ database: DB, statement, values, transaction: true, readonly: false });
  }

  private async getJson<T>(table: string, id: string): Promise<T | undefined> {
    const [row] = await this.rows(`SELECT data FROM ${table} WHERE id = ?`, [id]);
    return row ? (JSON.parse(String(row.data)) as T) : undefined;
  }

  private async allJson<T>(table: string, where = '', values: unknown[] = []): Promise<T[]> {
    return (await this.rows(`SELECT data FROM ${table} ${where}`, values)).map((r) => JSON.parse(String(r.data)) as T);
  }

  // ---- outbox ---------------------------------------------------------------------
  all(): Promise<OutboxItem[]> {
    return this.allJson<OutboxItem>('outbox', 'ORDER BY seq');
  }
  async put(item: OutboxItem): Promise<void> {
    await this.run(`INSERT OR REPLACE INTO outbox (id, seq, data) VALUES (?, ?, ?)`, [item.id, item.seq, JSON.stringify(item)]);
  }
  async remove(id: string): Promise<void> {
    await this.run(`DELETE FROM outbox WHERE id = ?`, [id]);
  }

  // ---- pack ---------------------------------------------------------------------------
  async replacePack(pack: SyncPack): Promise<void> {
    const pinned = new Set((await this.listDrafts()).map((d) => d.templateVersionId));
    const keep = (await this.allJson<PackTemplateVersion>('templates')).filter(
      (t) => pinned.has(t.versionId) && !pack.templates.some((p) => p.versionId === t.versionId),
    );
    const set: { statement: string; values: unknown[] }[] = [
      { statement: 'DELETE FROM sites', values: [] },
      { statement: 'DELETE FROM customers', values: [] },
      { statement: 'DELETE FROM templates', values: [] },
      { statement: 'DELETE FROM findings', values: [] },
      ...pack.sites.map((s) => ({ statement: 'INSERT INTO sites (id, data) VALUES (?, ?)', values: [s.id, JSON.stringify(s)] })),
      ...pack.customers.map((c) => ({ statement: 'INSERT INTO customers (id, data) VALUES (?, ?)', values: [c.id, JSON.stringify(c)] })),
      ...[...pack.templates, ...keep].map((t) => ({ statement: 'INSERT INTO templates (id, data) VALUES (?, ?)', values: [t.versionId, JSON.stringify(t)] })),
      ...(pack.carriedFindings ?? []).map((f) => ({
        statement: 'INSERT INTO findings (id, site_id, data) VALUES (?, ?, ?)',
        values: [f.id, f.siteId, JSON.stringify(f)],
      })),
      { statement: 'INSERT OR REPLACE INTO kv (key, value) VALUES (?, ?)', values: ['pack.serverTime', JSON.stringify(pack.serverTime)] },
      { statement: 'INSERT OR REPLACE INTO kv (key, value) VALUES (?, ?)', values: ['pack.downloadedAt', JSON.stringify(Date.now())] },
    ];
    await this.plugin.executeSet({ database: DB, set, transaction: true, readonly: false });
  }
  getSite(id: string) {
    return this.getJson<PackSite>('sites', id);
  }
  listSites() {
    return this.allJson<PackSite>('sites');
  }
  async searchSites(query: string, limit = 50) {
    return (await this.listSites())
      .filter((s) => siteMatches(s, query))
      .sort((a, b) => a.name.localeCompare(b.name))
      .slice(0, limit);
  }
  async putSites(sites: PackSite[]) {
    for (const s of sites) await this.run('INSERT OR REPLACE INTO sites (id, data) VALUES (?, ?)', [s.id, JSON.stringify(s)]);
  }
  getCustomer(id: string) {
    return this.getJson<PackCustomer>('customers', id);
  }
  async putCustomers(customers: PackCustomer[]) {
    for (const c of customers) await this.run('INSERT OR REPLACE INTO customers (id, data) VALUES (?, ?)', [c.id, JSON.stringify(c)]);
  }
  getTemplateVersion(versionId: string) {
    return this.getJson<PackTemplateVersion>('templates', versionId);
  }
  async putTemplateVersion(t: PackTemplateVersion) {
    await this.run('INSERT OR REPLACE INTO templates (id, data) VALUES (?, ?)', [t.versionId, JSON.stringify(t)]);
  }
  findingsForSite(siteId: string) {
    return this.allJson<CarriedFinding>('findings', 'WHERE site_id = ?', [siteId]);
  }

  // ---- drafts --------------------------------------------------------------------------
  async putDraft(d: DraftRecord) {
    await this.run('INSERT OR REPLACE INTO drafts (id, updated_at, data) VALUES (?, ?, ?)', [d.id, d.updatedAt, JSON.stringify(d)]);
  }
  getDraft(id: string) {
    return this.getJson<DraftRecord>('drafts', id);
  }
  listDrafts() {
    return this.allJson<DraftRecord>('drafts', 'ORDER BY updated_at DESC');
  }
  async deleteDraft(id: string) {
    await this.run('DELETE FROM drafts WHERE id = ?', [id]);
  }

  // ---- attachments ------------------------------------------------------------------
  async putAttachment(a: AttachmentRecord) {
    const { data, ...meta } = a;
    await this.run('INSERT OR REPLACE INTO attachments (id, survey_id, meta, body) VALUES (?, ?, ?, ?)', [
      a.id,
      a.surveyId,
      JSON.stringify(meta),
      toBase64(data),
    ]);
  }
  private fromRow(r: Record<string, unknown>): AttachmentRecord {
    return { ...(JSON.parse(String(r.meta)) as Omit<AttachmentRecord, 'data'>), data: fromBase64(String(r.body)) };
  }
  async getAttachment(id: string) {
    const [r] = await this.rows('SELECT meta, body FROM attachments WHERE id = ?', [id]);
    return r ? this.fromRow(r) : undefined;
  }
  async listAttachments(surveyId: string) {
    return (await this.rows('SELECT meta, body FROM attachments WHERE survey_id = ?', [surveyId])).map((r) => this.fromRow(r));
  }
  async deleteAttachment(id: string) {
    await this.run('DELETE FROM attachments WHERE id = ?', [id]);
  }

  // ---- kv --------------------------------------------------------------------------------
  async getMeta<T>(key: string): Promise<T | undefined> {
    const [r] = await this.rows('SELECT value FROM kv WHERE key = ?', [key]);
    return r ? (JSON.parse(String(r.value)) as T) : undefined;
  }
  async setMeta(key: string, value: unknown) {
    await this.run('INSERT OR REPLACE INTO kv (key, value) VALUES (?, ?)', [key, JSON.stringify(value)]);
  }

  async wipe(): Promise<void> {
    try {
      await this.plugin.execute({
        database: DB,
        statements: ['kv', 'sites', 'customers', 'templates', 'findings', 'drafts', 'attachments', 'outbox'].map((t) => `DELETE FROM ${t};`).join('\n'),
        transaction: true,
        readonly: false,
      });
    } finally {
      // Crypto-shred: remove the file and the secret even if the DELETEs failed.
      await this.plugin.deleteDatabase({ database: DB, readonly: false }).catch(() => {});
      await this.plugin.closeConnection({ database: DB, readonly: false }).catch(() => {});
      await this.plugin.clearEncryptionSecret();
      this.ready = false;
    }
    // Fresh secret and empty database for the next sign-in.
    await this.init();
  }
}
